import { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { User, Mail, Phone, MapPin, Shield, Calendar, Eye, EyeOff, Key, Camera, Trash2, Upload, Bell, Briefcase, HeartHandshake, Banknote } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { formatDate } from '@/utils/dateFormat';
import NotificationSettings from '@/components/NotificationSettings';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getAuthHeader = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
});

const serviceLength = (dateStr) => {
  if (!dateStr) return '';
  const start = new Date(dateStr);
  if (isNaN(start.getTime())) return '';
  const now = new Date();
  let months = (now.getFullYear() - start.getFullYear()) * 12 + (now.getMonth() - start.getMonth());
  if (now.getDate() < start.getDate()) months -= 1;
  if (months < 0) return '';
  const yrs = Math.floor(months / 12);
  const mths = months % 12;
  const parts = [];
  if (yrs > 0) parts.push(`${yrs} yr${yrs > 1 ? 's' : ''}`);
  parts.push(`${mths} mth${mths !== 1 ? 's' : ''}`);
  return parts.join(' ');
};

const Profile = () => {
  const [user, setUser] = useState(JSON.parse(localStorage.getItem('user') || '{}'));

  // Fetch fresh profile so HR details (updated by admin) show without needing re-login
  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/auth/me`, getAuthHeader());
        if (res.data) {
          setUser((prev) => ({ ...prev, ...res.data }));
          const stored = JSON.parse(localStorage.getItem('user') || '{}');
          localStorage.setItem('user', JSON.stringify({ ...stored, ...res.data }));
        }
      } catch (e) {
        // keep cached user on failure
      }
    })();
  }, []);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordData, setPasswordData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });
  const [loading, setLoading] = useState(false);
  const [photoLoading, setPhotoLoading] = useState(false);
  const fileInputRef = useRef(null);

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    // Validate file size (max 2MB)
    if (file.size > 2 * 1024 * 1024) {
      toast.error('Image size must be less than 2MB');
      return;
    }

    setPhotoLoading(true);
    try {
      // Convert to base64
      const reader = new FileReader();
      reader.onload = async (event) => {
        const base64Data = event.target?.result;
        
        try {
          const response = await axios.post(`${API}/auth/profile-photo`, { photo: base64Data }, getAuthHeader());
          
          // Update user in state and localStorage
          const updatedUser = { ...user, profile_photo: response.data.photo };
          setUser(updatedUser);
          localStorage.setItem('user', JSON.stringify(updatedUser));
          
          toast.success('Profile photo updated successfully');
        } catch (error) {
          toast.error(error.response?.data?.detail || 'Failed to upload photo');
        } finally {
          setPhotoLoading(false);
        }
      };
      reader.onerror = () => {
        toast.error('Failed to read image file');
        setPhotoLoading(false);
      };
      reader.readAsDataURL(file);
    } catch (error) {
      toast.error('Failed to process image');
      setPhotoLoading(false);
    }
  };

  const handleRemovePhoto = async () => {
    setPhotoLoading(true);
    try {
      await axios.delete(`${API}/auth/profile-photo`, getAuthHeader());
      
      // Update user in state and localStorage
      const updatedUser = { ...user, profile_photo: null };
      setUser(updatedUser);
      localStorage.setItem('user', JSON.stringify(updatedUser));
      
      toast.success('Profile photo removed');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove photo');
    } finally {
      setPhotoLoading(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    
    if (passwordData.new_password.length < 6) {
      toast.error('New password must be at least 6 characters');
      return;
    }
    
    if (passwordData.new_password !== passwordData.confirm_password) {
      toast.error('New passwords do not match');
      return;
    }
    
    setLoading(true);
    try {
      // First verify current password by attempting login
      const loginResponse = await axios.post(`${API}/auth/login`, {
        identifier: user.email || user.username,
        password: passwordData.current_password
      });
      
      if (!loginResponse.data.access_token) {
        toast.error('Current password is incorrect');
        setLoading(false);
        return;
      }
      
      // Now change the password
      await axios.post(`${API}/auth/change-password`, 
        { new_password: passwordData.new_password },
        getAuthHeader()
      );
      
      toast.success('Password changed successfully!');
      setShowChangePassword(false);
      setPasswordData({ current_password: '', new_password: '', confirm_password: '' });
    } catch (error) {
      if (error.response?.status === 401) {
        toast.error('Current password is incorrect');
      } else {
        toast.error(error.response?.data?.detail || 'Failed to change password');
      }
    } finally {
      setLoading(false);
    }
  };

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case 'admin': return 'bg-red-100 text-red-700 border-red-200';
      case 'manager': return 'bg-purple-100 text-purple-700 border-purple-200';
      case 'staff': return 'bg-blue-100 text-blue-700 border-blue-200';
      case 'distributor': return 'bg-amber-100 text-amber-700 border-amber-200';
      case 'customer': return 'bg-emerald-100 text-emerald-700 border-emerald-200';
      default: return 'bg-slate-100 text-slate-700 border-slate-200';
    }
  };

  return (
    <div className="space-y-6" data-testid="profile-page">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-4xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            My Profile
          </h1>
          <p className="text-lg text-slate-600 mt-2">View and manage your account information</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Profile Card */}
        <div className="lg:col-span-2">
          <div className="card p-6">
            <div className="flex items-start gap-6">
              {/* Avatar with Photo Upload */}
              <div className="relative group">
                {user.profile_photo ? (
                  <img 
                    src={user.profile_photo} 
                    alt={user.name} 
                    className="w-24 h-24 rounded-full object-cover shadow-lg"
                    data-testid="profile-photo"
                  />
                ) : (
                  <div className="w-24 h-24 rounded-full bg-gradient-to-br from-emerald-400 to-emerald-600 flex items-center justify-center text-white text-3xl font-bold shadow-lg" data-testid="profile-avatar">
                    {user.name?.charAt(0)?.toUpperCase() || 'U'}
                  </div>
                )}
                
                {/* Photo overlay controls */}
                <div className="absolute inset-0 rounded-full bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    disabled={photoLoading}
                    className="p-2 bg-white/90 rounded-full hover:bg-white transition-colors"
                    title="Upload photo"
                    data-testid="upload-photo-btn"
                  >
                    <Camera className="w-4 h-4 text-slate-700" />
                  </button>
                  {user.profile_photo && (
                    <button
                      onClick={handleRemovePhoto}
                      disabled={photoLoading}
                      className="p-2 bg-white/90 rounded-full hover:bg-white transition-colors"
                      title="Remove photo"
                      data-testid="remove-photo-btn"
                    >
                      <Trash2 className="w-4 h-4 text-red-600" />
                    </button>
                  )}
                </div>
                
                {/* Loading indicator */}
                {photoLoading && (
                  <div className="absolute inset-0 rounded-full bg-black/50 flex items-center justify-center">
                    <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  </div>
                )}
                
                {/* Hidden file input */}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handlePhotoUpload}
                  className="hidden"
                  data-testid="photo-file-input"
                />
              </div>
              
              {/* Info */}
              <div className="flex-1">
                <h2 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  {user.name || 'User'}
                </h2>
                <div className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium border mt-2 ${getRoleBadgeColor(user.role)}`}>
                  <Shield className="w-3.5 h-3.5" />
                  {user.role?.charAt(0).toUpperCase() + user.role?.slice(1) || 'User'}
                </div>
                
                <div className="mt-4 space-y-3">
                  {user.email && (
                    <div className="flex items-center gap-3 text-slate-600">
                      <Mail className="w-5 h-5 text-slate-400" />
                      <span>{user.email}</span>
                    </div>
                  )}
                  {user.username && (
                    <div className="flex items-center gap-3 text-slate-600">
                      <User className="w-5 h-5 text-slate-400" />
                      <span>@{user.username}</span>
                    </div>
                  )}
                  {user.phone && (
                    <div className="flex items-center gap-3 text-slate-600">
                      <Phone className="w-5 h-5 text-slate-400" />
                      <span>{user.phone}</span>
                    </div>
                  )}
                  {user.address && (
                    <div className="flex items-center gap-3 text-slate-600">
                      <MapPin className="w-5 h-5 text-slate-400" />
                      <span>{user.address}</span>
                    </div>
                  )}
                  {user.created_at && (
                    <div className="flex items-center gap-3 text-slate-600">
                      <Calendar className="w-5 h-5 text-slate-400" />
                      <span>Member since {formatDate(user.created_at)}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Employment Details */}
          {(user.date_of_hire || user.next_of_kin_name || user.next_of_kin_phone || user.emergency_contact_name || user.emergency_contact_phone || (user.starting_salary !== null && user.starting_salary !== undefined && user.starting_salary !== '')) && (
            <div className="card p-6 mt-6" data-testid="employment-details-card">
              <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
                <Briefcase className="w-5 h-5 text-emerald-600" />
                Employment Details
              </h3>
              <div className="space-y-3">
                {user.date_of_hire && (
                  <div className="flex items-center gap-3 text-slate-600" data-testid="profile-date-of-hire">
                    <Calendar className="w-5 h-5 text-slate-400" />
                    <span><span className="text-slate-400">Date of Hire:</span> {formatDate(user.date_of_hire)} <span className="text-slate-400">({serviceLength(user.date_of_hire)})</span></span>
                  </div>
                )}
                {(user.next_of_kin_name || user.next_of_kin_phone) && (
                  <div className="flex items-start gap-3 text-slate-600" data-testid="profile-next-of-kin">
                    <HeartHandshake className="w-5 h-5 text-slate-400 mt-0.5" />
                    <span>
                      <span className="text-slate-400">Next of Kin:</span> {user.next_of_kin_name || '—'}
                      {user.next_of_kin_phone && <span className="text-slate-500"> · {user.next_of_kin_phone}</span>}
                    </span>
                  </div>
                )}
                {(user.emergency_contact_name || user.emergency_contact_phone) && (
                  <div className="flex items-start gap-3 text-slate-600" data-testid="profile-emergency-contact">
                    <Phone className="w-5 h-5 text-slate-400 mt-0.5" />
                    <span>
                      <span className="text-slate-400">Emergency Contact:</span> {user.emergency_contact_name || '—'}
                      {user.emergency_contact_phone && <span className="text-slate-500"> · {user.emergency_contact_phone}</span>}
                    </span>
                  </div>
                )}
                {user.starting_salary !== null && user.starting_salary !== undefined && user.starting_salary !== '' && (
                  <div className="flex items-center gap-3 text-slate-600" data-testid="profile-starting-salary">
                    <Banknote className="w-5 h-5 text-slate-400" />
                    <span><span className="text-slate-400">Starting Salary:</span> ₦{Number(user.starting_salary).toLocaleString()}</span>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Actions Card */}
        <div className="lg:col-span-1">
          <div className="card p-6">
            <h3 className="text-lg font-semibold text-slate-900 mb-4">Account Actions</h3>
            
            <div className="space-y-3">
              <Dialog open={showChangePassword} onOpenChange={setShowChangePassword}>
                <DialogTrigger asChild>
                  <Button 
                    data-testid="change-password-button"
                    className="w-full bg-emerald-600 hover:bg-emerald-700"
                  >
                    <Key className="w-4 h-4 mr-2" />
                    Change Password
                  </Button>
                </DialogTrigger>
                <DialogContent data-testid="change-password-dialog">
                  <DialogHeader>
                    <DialogTitle>Change Password</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleChangePassword} className="space-y-4">
                    <div>
                      <Label htmlFor="current-password">Current Password</Label>
                      <div className="relative">
                        <Input
                          id="current-password"
                          type={showCurrentPassword ? 'text' : 'password'}
                          data-testid="current-password-input"
                          value={passwordData.current_password}
                          onChange={(e) => setPasswordData({ ...passwordData, current_password: e.target.value })}
                          placeholder="Enter current password"
                          required
                        />
                        <button
                          type="button"
                          onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                          className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-slate-600"
                        >
                          {showCurrentPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                    </div>
                    
                    <div>
                      <Label htmlFor="new-password">New Password</Label>
                      <div className="relative">
                        <Input
                          id="new-password"
                          type={showNewPassword ? 'text' : 'password'}
                          data-testid="new-password-input"
                          value={passwordData.new_password}
                          onChange={(e) => setPasswordData({ ...passwordData, new_password: e.target.value })}
                          placeholder="Enter new password (min 6 characters)"
                          required
                          minLength={6}
                        />
                        <button
                          type="button"
                          onClick={() => setShowNewPassword(!showNewPassword)}
                          className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-slate-600"
                        >
                          {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                    </div>
                    
                    <div>
                      <Label htmlFor="confirm-password">Confirm New Password</Label>
                      <div className="relative">
                        <Input
                          id="confirm-password"
                          type={showConfirmPassword ? 'text' : 'password'}
                          data-testid="confirm-password-input"
                          value={passwordData.confirm_password}
                          onChange={(e) => setPasswordData({ ...passwordData, confirm_password: e.target.value })}
                          placeholder="Confirm new password"
                          required
                        />
                        <button
                          type="button"
                          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                          className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-slate-600"
                        >
                          {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                    </div>
                    
                    <div className="flex gap-2 pt-2">
                      <Button 
                        type="submit" 
                        className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                        disabled={loading}
                        data-testid="submit-password-change"
                      >
                        {loading ? 'Changing...' : 'Change Password'}
                      </Button>
                      <Button 
                        type="button" 
                        variant="outline" 
                        onClick={() => {
                          setShowChangePassword(false);
                          setPasswordData({ current_password: '', new_password: '', confirm_password: '' });
                        }}
                      >
                        Cancel
                      </Button>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>
            </div>
            
            <div className="mt-6 p-4 bg-slate-50 rounded-lg">
              <p className="text-sm text-slate-600">
                <strong>Account Status:</strong>{' '}
                {user.is_approved !== false ? (
                  <span className="text-emerald-600 font-medium">Active</span>
                ) : (
                  <span className="text-amber-600 font-medium">Pending Approval</span>
                )}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Account Details */}
      <div className="card p-6">
        <h3 className="text-lg font-semibold text-slate-900 mb-4">Account Details</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <div>
            <p className="text-sm text-slate-500 mb-1">Full Name</p>
            <p className="font-medium text-slate-900">{user.name || '-'}</p>
          </div>
          <div>
            <p className="text-sm text-slate-500 mb-1">Email Address</p>
            <p className="font-medium text-slate-900">{user.email || '-'}</p>
          </div>
          <div>
            <p className="text-sm text-slate-500 mb-1">Username</p>
            <p className="font-medium text-slate-900">{user.username ? `@${user.username}` : '-'}</p>
          </div>
          <div>
            <p className="text-sm text-slate-500 mb-1">Phone Number</p>
            <p className="font-medium text-slate-900">{user.phone || '-'}</p>
          </div>
          <div>
            <p className="text-sm text-slate-500 mb-1">Role</p>
            <p className="font-medium text-slate-900 capitalize">{user.role || '-'}</p>
          </div>
          <div>
            <p className="text-sm text-slate-500 mb-1">Address</p>
            <p className="font-medium text-slate-900">{user.address || '-'}</p>
          </div>
        </div>
      </div>

      {/* Notification Settings Section */}
      <div className="card p-6">
        <h2 className="text-xl font-semibold text-slate-900 mb-4 flex items-center gap-2">
          <Bell className="w-5 h-5 text-emerald-600" />
          Notification Settings
        </h2>
        <NotificationSettings />
      </div>
    </div>
  );
};

export default Profile;
